import React from 'react';
import type { MarkdownToJSX } from 'markdown-to-jsx';
import { PayloadCodeBlock } from './PayloadCodeBlock';
import { detectPayloadLanguage, PayloadLanguage } from './payloadBlocks';

type PreProps = { children?: React.ReactNode; className?: string };

function isPayloadLanguage(lang: string | undefined): lang is PayloadLanguage {
  return lang === 'xml' || lang === 'json';
}

/** markdown-to-jsx renders every fenced code block as `<pre><code
 *  className="lang-xxx">...</code></pre>` (see the `codeBlock`/`codeFenced`
 *  handlers it ships). Overriding `pre` lets us swap in the boxed
 *  PayloadCodeBlock for XML/JSON — by the fence's own ```xml/```json tag, or
 *  by sniffing the content when the agent (or `wrapRawPayloadBlocks`) left it
 *  unlabeled — while leaving every other language's block as a plain
 *  `<pre><code>` so the existing generic code styling still applies. */
const PayloadAwarePre: React.FC<PreProps> = ({ children, className }) => {
  const codeChild = Array.isArray(children) ? children[0] : children;
  if (React.isValidElement<{ className?: string; children?: React.ReactNode }>(codeChild)) {
    const fencedLang = /lang-(\w+)/.exec(codeChild.props.className ?? '')?.[1]?.toLowerCase();
    const raw = codeChild.props.children;
    const content =
      typeof raw === 'string' ? raw : Array.isArray(raw) ? raw.join('') : String(raw ?? '');
    // Only sniff the content when the fence carries no language label at all.
    // An explicit, non-xml/json label (```html, ```javascript, ...) is the
    // author's own word for what this is — it must win over a same-shaped
    // sniff, or a labeled JSON-shaped ```javascript block would get rendered
    // and downloaded as JSON.
    const language = fencedLang
      ? isPayloadLanguage(fencedLang)
        ? fencedLang
        : null
      : detectPayloadLanguage(content);
    if (language) {
      return <PayloadCodeBlock language={language} content={content} />;
    }
  }
  return <pre className={className}>{children}</pre>;
};

/** Agent (and subagent) replies routinely quote raw XML/JSON payloads (SOAP
 *  bodies, log lines) inline, not fenced in a code block. markdown-to-jsx's
 *  default behavior is to parse embedded `<...>` runs as HTML tag soup —
 *  namespaced/malformed tags like `<soapenv:Envelope>` break that parse and
 *  the whole payload silently vanishes instead of rendering.
 *  `disableParsingRawHTML` is the safety net for that (makes it literal text
 *  instead); `wrapRawPayloadBlocks` (called at the render site, see
 *  TriageChat.tsx/TriageSubagentBubble.tsx) promotes the common case — a
 *  payload alone in its own paragraph — into the boxed PayloadCodeBlock via
 *  the `pre` override above. */
export const rawPayloadMarkdownOptions: MarkdownToJSX.Options = {
  disableParsingRawHTML: true,
  overrides: { pre: { component: PayloadAwarePre } },
};
